import type { ApiEnvelope, ResultCode } from '@cg/shared';

/**
 * API 客户端:统一信封、业务码、traceId、令牌与刷新、租户头。
 * 页面只处理 data;非 0 业务码抛 ApiError,由调用方或全局拦截决定提示。
 */
export class ApiError extends Error {
  constructor(
    readonly code: ResultCode | string,
    message: string,
    readonly data: unknown,
    readonly traceId: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientOptions {
  baseUrl?: string;
  tenantCode?: () => string | undefined;
  accessToken?: () => string | undefined;
  /** 令牌过期(A0102)时调用一次;返回新令牌则重试原请求 */
  refresh?: () => Promise<string | undefined>;
  fetchImpl?: typeof fetch;
  /** 端标识(x-client);给函数时每次请求现取(学生端在手机版式下是 h5,见 stores/viewport) */
  client?: ClientKind | (() => ClientKind);
}
export type ClientKind = 'web' | 'h5' | 'miniapp' | 'pc_client';

export interface RequestInitEx extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

/** 站点闸门的业务码(设计图 24):系统维护 / 机构只读 / 配额已满 */
/** 站点闸门类结果码:维护中 / 机构只读 / 配额已满 / 模块未开通 / 模块到期只读(后两个刷新站点的模块与菜单) */
export const GATE_CODES = ['S0003', 'T0101', 'T0103', 'T0102', 'T0104'];
export interface GateEvent {
  code: string;
  msg: string;
  data: { until?: string | null; status?: string } | null;
}

/**
 * 本浏览器的随机设备 ID(存 localStorage,第一次用时生成):后台登录认「新设备」要二次验证;
 * 服务端只存它的哈希。隐私模式拿不到 localStorage 时每次都是新设备(照常二次验证)。
 */
let cachedDevice: string | null = null;
export function deviceId(): string | null {
  if (cachedDevice) return cachedDevice;
  try {
    let v = localStorage.getItem('cg.device');
    if (!v || !/^[A-Za-z0-9_-]{16,64}$/.test(v)) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      v = btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      localStorage.setItem('cg.device', v);
    }
    cachedDevice = v;
    return v;
  } catch {
    return null;
  }
}

export function createClient(opts: ClientOptions = {}) {
  const base = (opts.baseUrl ?? '').replace(/\/$/, '');
  const fetchImpl = opts.fetchImpl ?? fetch;
  const clientKind = (): ClientKind =>
    (typeof opts.client === 'function' ? opts.client() : opts.client) ?? 'web';

  async function request<T>(
    method: string,
    path: string,
    init: RequestInitEx = {},
    retried = false,
  ): Promise<T> {
    const url = new URL(base + path, typeof location !== 'undefined' ? location.origin : 'http://localhost');
    for (const [k, v] of Object.entries(init.query ?? {}))
      if (v !== undefined) url.searchParams.set(k, String(v));
    const headers = new Headers(init.headers);
    headers.set('accept', 'application/json');
    headers.set('x-client', clientKind());
    // 设备 ID 只在认证接口上带(认新设备),别的请求不需要
    const dev = path.startsWith('/v1/auth/') ? deviceId() : null;
    if (dev) headers.set('x-device-id', dev);
    const tenant = opts.tenantCode?.();
    if (tenant) headers.set('x-tenant-code', tenant);
    const token = opts.accessToken?.();
    if (token) headers.set('authorization', `Bearer ${token}`);
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
      if (init.body instanceof FormData || init.body instanceof Blob) body = init.body;
      else {
        headers.set('content-type', 'application/json');
        body = JSON.stringify(init.body);
      }
    }
    const res = await fetchImpl(url.toString(), { ...init, method, headers, body });
    const traceId = res.headers.get('x-trace-id') ?? '';
    let env: ApiEnvelope<T> | undefined;
    try {
      env = (await res.json()) as ApiEnvelope<T>;
    } catch {
      throw new ApiError('S0002', `响应不是 JSON(HTTP ${res.status})`, null, traceId, res.status);
    }
    if (env.code === '0') return env.data;
    if (env.code === 'A0102' && opts.refresh && !retried) {
      const fresh = await opts.refresh();
      if (fresh) return request<T>(method, path, init, true);
    }
    // 维护中 / 机构只读:广播给应用外壳(去维护页、显示只读横幅),调用方照常拿到错误
    if (GATE_CODES.includes(env.code) && typeof window !== 'undefined')
      window.dispatchEvent(
        new CustomEvent('cg:gate', { detail: { code: env.code, msg: env.msg, data: env.data } }),
      );
    // 后台账号还在用初始密码(接口层 A0105):外壳弹不可关的「请先修改密码」
    if (env.code === 'A0105' && typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent('cg:pwc'));
    throw new ApiError(env.code, env.msg, env.data, env.traceId || traceId, res.status);
  }

  /**
   * 下载非 JSON 响应(CSV 导出等):成功给 Blob;失败时服务端仍回信封(非 2xx),按业务码抛 ApiError。
   * raw:取的东西本身就是 JSON 文件(作品的积木结构等),成功响应原样给,不当信封看
   */
  async function blob(path: string, o: { raw?: boolean } = {}, retried = false): Promise<Blob> {
    const url = new URL(base + path, typeof location !== 'undefined' ? location.origin : 'http://localhost');
    const headers = new Headers();
    headers.set('x-client', clientKind());
    const tenant = opts.tenantCode?.();
    if (tenant) headers.set('x-tenant-code', tenant);
    const token = opts.accessToken?.();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetchImpl(url.toString(), { method: 'GET', headers });
    const type = res.headers.get('content-type') ?? '';
    if (res.ok && (o.raw || !type.includes('application/json'))) return res.blob();
    const env = (await res.json().catch(() => null)) as ApiEnvelope<unknown> | null;
    if (env?.code === 'A0102' && opts.refresh && !retried) {
      const fresh = await opts.refresh();
      if (fresh) return blob(path, o, true);
    }
    throw new ApiError(
      env?.code ?? 'S0002',
      env?.msg ?? `下载失败(HTTP ${res.status})`,
      null,
      env?.traceId ?? '',
      res.status,
    );
  }

  return {
    blob,
    /** 接口路径 → 完整地址(直传票给的本地中转地址是相对路径,用原生 fetch 传、不带令牌) */
    resolve: (path: string) =>
      new URL(base + path, typeof location !== 'undefined' ? location.origin : 'http://localhost').toString(),
    get: <T>(path: string, init?: RequestInitEx) => request<T>('GET', path, init),
    post: <T>(path: string, body?: unknown, init?: RequestInitEx) =>
      request<T>('POST', path, { ...init, body }),
    patch: <T>(path: string, body?: unknown, init?: RequestInitEx) =>
      request<T>('PATCH', path, { ...init, body }),
    put: <T>(path: string, body?: unknown, init?: RequestInitEx) =>
      request<T>('PUT', path, { ...init, body }),
    delete: <T>(path: string, init?: RequestInitEx) => request<T>('DELETE', path, init),
  };
}

export type ApiClient = ReturnType<typeof createClient>;
